import _ from 'underscore';
import abl from '../../abl';
import Time from '../../lib/time';

const GoogleSheetProcessor = abl.createClass({
	DAY_OF_WEEK_MAP: {
		"M": "Monday",
		"T": "Tuesday",
		"W": "Wednesday",
		"Th": "Thursday",
		"F": "Friday"
	},

	initialize: function(result, version = 1.0) {
		this.name = null;
		this.id = null;
		this.teachers = [];
		this.cohorts = [];
		this.meetings = [];
		this.scheduledMeetings = [];
		this.version = version;
		this._processData(result);
	},

	toJSON: function() {
		return {
			name: this.name,
			id: this.id,
			teachers: this.teachers,
		  cohorts: this.cohorts,
			meetings: this.meetings,
			scheduledMeetings: this.scheduledMeetings
		};
	},

	_processData: function(result) {
		switch(this.version) {
			case 1.0:
				this._processV1Data(result);
				break;
			case 2.0:
				this._processV2Data(result);
				break;
			default:
				console.log('Unrecognized sheet version!');
		}
	},

	_processV1Data: function(result) {
		if(result.sheets.length >= 3) {
			this.id = result.spreadsheetId;
			this.name = result.properties.title;
			this._processSchedules(this._selectSheet(result.sheets, 'School'), this.scheduledMeetings);
			this._createObjectsFromSheet(
				this._selectSheet(result.sheets, 'Teachers'),
				this.teachers,
				this._processTeacher
			);
			this._createObjectsFromSheet(this._selectSheet(result.sheets, 'Meetings'), this.meetings);
			this.cohorts = _.chain(this.scheduledMeetings)
				.map(meeting => meeting.cohort_ids)
				.flatten()
				.uniq()
				.compact()
				.map(cohort_id => Object.assign({id: cohort_id}))
				.value()
		}
	},

	_processV2Data: function(result) {
		if(result.sheets.length >= 3) {
			this.id = result.spreadsheetId;
			this.name = result.properties.title;
			this._processScheduledMeetings(this._selectSheet(result.sheets, 'School'), this.scheduledMeetings);
			this._createObjectsFromSheet(
				this._selectSheet(result.sheets, 'Staff'),
				this.teachers,
				this._processTeacher
			);
			this._createObjectsFromSheet(
				this._selectSheet(result.sheets, 'Cohorts'),
				this.cohorts,
				this._processCohort
			);
			this._createObjectsFromSheet(this._selectSheet(result.sheets, 'Meetings'), this.meetings);
		}
	},

	_selectSheet: function(sheetCollection, sheetName) {
		return _.find(sheetCollection, function(sheet) { return sheet.properties.title === sheetName });
	},

	_scheduledMeetingsForParticipant: function(participant, associationName) {
		return this.scheduledMeetings.filter(sm =>
			sm[associationName].includes(participant.id)
		)
	},

	_sumOfScheduledMeetingMinutes: function(scheduledMeetings, filter = (scheduledMeeting => scheduledMeeting)) {
		return scheduledMeetings.filter(
			filter
		).map(
			sm => sm.duration_minutes
		).reduce(
			(prev, current, i, array) => (prev + current)
		);
	},

	_processTeacher: function(teacher) {
		if(!teacher.name) {
			teacher.name = [teacher.first_name, teacher.last_name].join(" ");
		}

		let scheduledMeetings = this._scheduledMeetingsForParticipant(teacher, 'teacher_ids');

		if (scheduledMeetings.length > 0) {
			teacher.scheduled_meeting_ids = scheduledMeetings.map(sm => sm.id);
			teacher.total_minutes = this._sumOfScheduledMeetingMinutes(scheduledMeetings);
			teacher.scheduled_minutes = this._sumOfScheduledMeetingMinutes(scheduledMeetings, (sm) => {
				return (sm.meeting_id !== null && sm.meeting_id !== 'Prep');
			});
			teacher.unscheduled_minutes = teacher.total_minutes - teacher.scheduled_minutes;
		}
		
		if(teacher.prep_teachers) {
			teacher.prep_teachers = teacher.prep_teachers.split(',');
		}

		if(teacher.release_teachers) {
			teacher.release_teachers = teacher.release_teachers.split(',');
		}

		if(teacher.unavailable) {
			teacher.unavailable = teacher.unavailable.split(',');
		}
	},

	_getFormattedValue: function(cellData) {
		if(cellData && cellData.formattedValue) {
			return cellData.formattedValue.trim();
		} else {
			return null
		}
	},

	_processScheduledMeetings: function(sheet, collection) {
		if(!sheet) {
			console.log('Could not parse schedule data');
		}

		let rows = sheet.data[0].rowData;
		let columnIndexes = this._getColumnIndexes(rows[0]);
		rows.shift();

		for(let row of rows) {
			if(row.values[0].formattedValue) {
				let startTime = this._processTime(this._getFormattedValue(
					row.values[columnIndexes.start_time])
				);

				let endTime = this._processTime(
					this._getFormattedValue(row.values[columnIndexes.end_time])
				);

				let durationMinutes = Time.duration(startTime, endTime).minutes;

				let teacherIds = columnIndexes.teacher_id.reduce((ids, index) => {
					ids = ids.concat(this._parseParticipantList(
						this._getFormattedValue(row.values[index])
					));
					
					return ids;
				}, []);

				let cohortIds = columnIndexes.section_id.reduce((ids, index) => {
					ids = ids.concat(this._parseParticipantList(
						this._getFormattedValue(row.values[index])
					));
					return ids;
				}, []);

				let meetingName = this._getFormattedValue(
					row.values[columnIndexes.name]
				);

				let meetingTypeId = this._getFormattedValue(
					row.values[columnIndexes.meeting_type_id]
				);

				let daysOfWeek = this._parseDaysOfWeekRanges(
					this._getFormattedValue(row.values[columnIndexes.day_of_week])
				);

				let period = this._getFormattedValue(
					row.values[columnIndexes.period]
				);

				for(let dayOfWeek of daysOfWeek) {
					collection.push({
						id: collection.length + 1,
						day_of_week: dayOfWeek,
						days_of_week: daysOfWeek,
						start_time: startTime,
						end_time: endTime,
						duration_minutes: durationMinutes,
						teacher_ids: teacherIds,
						cohort_ids: cohortIds,
						name: meetingName,
						meeting_id: meetingTypeId,
						period: period
					});
				}
			}
		}

		return collection;
	},

	_getColumnIndexes: function(firstRow) {
		let columns = firstRow ? firstRow.values : [];
		return columns.reduce((columnNames, column, i) => {
			if(column.formattedValue) {
				if(columnNames[column.formattedValue]) {
					if(!columnNames[column.formattedValue].length) {
						columnNames[column.formattedValue] = [columnNames[column.formattedValue]];
					}
					columnNames[column.formattedValue].push(i);
				}
				else {
					columnNames[column.formattedValue] = i;	
				}
			}
			return columnNames;
		}, {});
	},

	_parseParticipantList: function(participantList) {
		if(!participantList) {
			return []
		}
		return participantList.split(/,\s*/)
	},

	_parseDaysOfWeekRanges: function(daysRanges) {
		if(!daysRanges) return [];
		let ranges = daysRanges.split(/,\s*/);
		let dayCodes = _.flatten(
			_.map(ranges, range => {
				if(!range.includes('-')) return range;
				let rangeEnds = range.split('-');
				let allDayCodes = _.keys(this.DAY_OF_WEEK_MAP);

				return allDayCodes.slice(
					allDayCodes.indexOf(rangeEnds[0]),
					allDayCodes.indexOf(rangeEnds[1]) + 1
				);
			})
		);
		return _.map(dayCodes, dayCode =>
			this.DAY_OF_WEEK_MAP[dayCode] ?
				this.DAY_OF_WEEK_MAP[dayCode] :
				dayCode
		);
	},

	_getMeetingKey: function(scheduledMeeting) {
	  return _.flatten([
	    scheduledMeeting.day_of_week,
	    scheduledMeeting.teacher_ids
	  ]).join('_');
	},

	_processSchedules: function(sheet, collection) {
		if(!sheet) {
			console.log('Could not parse schedule data');
		}

		let rows = sheet.data[0].rowData;
		let days = this._getDaysOfWeek(rows[0].values);
		let meetingsByKey = {};
		let processedMeetings = [];

		for(let i=2; i < rows.length; i++) {
			if(rows[i].values[0].formattedValue) {
				let timeBlock = rows[i].values[0].formattedValue.split('-');
				let startTime = this._processTime(timeBlock[0]);
				let endTime = this._processTime(timeBlock[1]);
				let durationMinutes = Time.duration(startTime, endTime).minutes;
				let teacherId = rows[i].values[1].formattedValue;

				for(let j=2; j < rows[i].values.length; j+=2) {
					let meetingName = rows[i].values[j];
					let cohortId = rows[i].values[j+1].formattedValue;
					if(cohortId === "0") cohortId = null;

					let dayOfWeek = days[j-2];

					let scheduledMeeting = Object.assign({},
						{
							id: processedMeetings.length + 1,
							day_of_week: dayOfWeek,
							days_of_week: [dayOfWeek],
							start_time: startTime,
							end_time: endTime,
							duration_minutes: durationMinutes,
							teacher_ids: _.compact([teacherId]),
							cohort_ids: _.compact([cohortId]),
							sheet_references: [
								{ row: i + 1, column: j + 1 }
							]
						},
						this._processMeetingValue(meetingName)
					)

					let key = this._getMeetingKey(scheduledMeeting);
					let meetingsForKey = meetingsByKey[key] = meetingsByKey[key] || [];
					let prevMeeting = _.last(meetingsForKey);
			    if(prevMeeting &&
			    		prevMeeting.meeting_id === scheduledMeeting.meeting_id &&
			    		prevMeeting.cohort_ids.join("_") === scheduledMeeting.cohort_ids.join("_")) {
			      prevMeeting.sheet_references.push(scheduledMeeting.sheet_references[0]);
			      prevMeeting.end_time = scheduledMeeting.end_time;
			    }
			    else {
			      meetingsForKey.push(scheduledMeeting);
			      processedMeetings.push(scheduledMeeting);
			    }
				}
			}
		}

		let groupedCollection = _.values(
			_.groupBy(processedMeetings, meeting => {
				if(meeting.cohort_ids.length === 0) {
					return meeting.id;
				}
				return _.flatten(
					_.values(
						_.pick(meeting, "day_of_week", "meeting_id", "start_time", "end_time", "cohort_ids")
					)
				).join("_")
			})
		);

		let compiledCollection = _.map(groupedCollection, meetings => {
			let newMeeting;
			if(meetings.length === 1) {
				newMeeting = _.first(meetings);
			} else {
				newMeeting = _.reduce(meetings, (aggregateMeeting, componentMeeting) => {
					aggregateMeeting.teacher_ids =
						aggregateMeeting.teacher_ids.concat(componentMeeting.teacher_ids);
					aggregateMeeting.sheet_references =
						aggregateMeeting.sheet_references.concat(componentMeeting.sheet_references);
					return aggregateMeeting;
				});
			}
			return newMeeting;
		});

		collection.push(...compiledCollection);
	},

	_getDaysOfWeek: function(values) {
		let days = [];
		let last;
		let value;
		
		// Need this because of merged cells
		for(let i=2; i < values.length; i++) {
			value = values[i].formattedValue ? values[i].formattedValue : null;
			if(value && last !== value) {
				last = value;
			}
			days.push(last);
		}
		return days;
	},

	_processMeetingValue: function(value) {
		value = value && value.formattedValue ? value.formattedValue : null;
		let id = value === '0' || value === '--' ? null : value;
		return {
			meeting_id: id,
			name: id,
			unavailable: value === '0' || value === '--',
			earlyRelease: value === '--'
		};
	},

	_processTime: function(timeStr) {
		let str = timeStr.replace(/^\s*/, '').replace(/\s*$/, '');
		let hour = parseInt(str.split(':')[0], 10);
		if(hour === 12 || hour < 6) {
		  str += 'pm'
		}
		else {
		  str += 'am'
		}
		return str;
	},

	_createObjectsFromSheet: function(sheet, collection, callback) {
		if(!sheet) {
			console.log('Could not parse sheet data');
		}
		let rows = sheet.data[0].rowData;
		let obj;
		
		callback = callback || function() {};
		for(let i=1; i < rows.length; i++){
			obj = {};
			for(let j=0; j < rows[i].values.length; j++) {
				obj[rows[0].values[j].formattedValue] = abl.convertString(rows[i].values[j] ? rows[i].values[j].formattedValue : null);
			}
			callback.bind(this)(obj);
			collection.push(obj);
		}
		return collection;
	}

});

export default GoogleSheetProcessor;


/** WEBPACK FOOTER **
 ** ./src/js/clients/google/google_sheet_processor.js
 **/